/**
 * Utility to clean old localStorage data from previous Supabase
 *
 * This is used when migrating to a new Supabase project to ensure
 * no stale data from the old project interferes with the new one.
 */

const KEYS_TO_CLEAN = [
  // Old patient data
  'psicosys_patients',
  'psicosys_sessions',
  'psicosys_appointments',

  // Old auth data
  'psicosys_auth_data',
  'user',
  'legacyUserId',
  'organizationId',

  // Old sync data
  'psicosys_last_sync',
  'psicosys_sync_status',
];

const BACKUP_PREFIX = 'psicosys_backup_';
const CLEANUP_FLAG = 'psicosys_cleaned_v2';

/**
 * Clean all old localStorage data from previous Supabase project
 * @returns Number of keys removed
 */
export function cleanOldLocalStorage(): number {
  let removedCount = 0;

  try {
    // Get all keys from localStorage
    const allKeys = Object.keys(localStorage);

    // Remove exact matches
    for (const key of KEYS_TO_CLEAN) {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
        removedCount++;
        console.log(`Removed: ${key}`);
      }
    }

    // Remove all backup keys
    for (const key of allKeys) {
      if (key.startsWith(BACKUP_PREFIX)) {
        localStorage.removeItem(key);
        removedCount++;
        console.log(`Removed backup: ${key}`);
      }
    }

    if (removedCount > 0) {
      console.log(`localStorage cleaned - ${removedCount} keys removed`);
    } else {
      console.log('localStorage already clean');
    }

    return removedCount;
  } catch (error) {
    console.error('Error cleaning localStorage:', error);
    return 0;
  }
}

/**
 * Check if cleanup was already performed
 */
export function isCleanupDone(): boolean {
  return localStorage.getItem(CLEANUP_FLAG) === 'true';
}

/**
 * Mark cleanup as done
 */
export function markCleanupDone(): void {
  localStorage.setItem(CLEANUP_FLAG, 'true');
}

/**
 * Run cleanup if not already done
 * @returns true if cleanup was performed, false if already done
 */
export function runCleanupOnce(): boolean {
  if (isCleanupDone()) {
    return false;
  }

  cleanOldLocalStorage();
  markCleanupDone();
  return true;
}

/**
 * Force cleanup regardless of previous state
 */
export function forceCleanup(): number {
  localStorage.removeItem(CLEANUP_FLAG);
  const removed = cleanOldLocalStorage();
  markCleanupDone();
  return removed;
}

const cleanOldDataUtils = {
  cleanOldLocalStorage,
  isCleanupDone,
  markCleanupDone,
  runCleanupOnce,
  forceCleanup,
};

export default cleanOldDataUtils;
